import { Component, OnInit } from '@angular/core';
import { AfterViewInit } from '@angular/core';
import { OnDestroy } from '@angular/core';
import { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { iif } from 'rxjs';
import { combineLatest } from 'rxjs';
import { SiteDto } from '../../../dtos/siteDto';
import { SitesDataService } from '../data-storage/sites-data.service';
import { GlobalDataService } from '../../../shared-services/global-data.service';
import { map } from 'rxjs/operators';
import { tap } from 'rxjs/operators';
import { switchMap } from 'rxjs/operators';
import { skipWhile } from 'rxjs/operators';
import { GeoJson } from '../../../vo/geoJson';
import { MapService } from './map.service';
import { UserDataService } from '../data-storage/user-data.service';
import { UserService } from '../services/user.service';
import { TranslateService } from "@ngx-translate/core";
import { MonumentDto } from '../../../dtos/monumentDto';
import { MonumentsDataService } from '../data-storage/monuments-data.service';
import * as mapboxgl from 'mapbox-gl';
import { SubscriptionPacketsService } from '../services/subscription-packets.service';
import { of } from 'rxjs/observable/of';

@Component({
    selector: 'app-map-page',
    templateUrl: './map-page.component.html',
    styleUrls: ['./map-page.component.scss']
})
export class MapPageComponent implements OnInit, AfterViewInit, OnDestroy {

    $sites: Observable<SiteDto[]>;
    $monuments: Observable<MonumentDto[]>
    geoJson: GeoJson;
    activeTab: number = 1;
    private mapListeners = {
        sites: new Subject<SiteDto>(),
        monuments: new Subject()
    }
    selectedSite: Observable<SiteDto>;
    selectedMonument: Observable<MonumentDto>;
    currentMonument: MonumentDto;
    currentSite: SiteDto;
    flowLoading: boolean;
    isMonumentOpen: boolean;
    isPacketOpen: boolean;
    isDrawerClosed = false;
    isLoginOpen = false;
    mapMonumentsToSite: Record<number, number>;
    private map;
    private mapPopup = {
        sites: undefined,
        monuments: undefined
    };

    constructor(private sitesDataService: SitesDataService,
                private monumentsDataService: MonumentsDataService,
                public globalDataService: GlobalDataService,
                public translateService: TranslateService,
                private mapService: MapService,
                private userDataService: UserDataService,
                private userService: UserService,
                private subscriptionPacketsService: SubscriptionPacketsService) {
    }

    ngOnInit(): void {

        this.translateService.onLangChange.subscribe(() => {
            if (this.monumentsDataService.getMonumentData().length > 0) {
                this.initMonuments();
            }
        })
        this.selectedSite = this.mapListeners.sites.asObservable()
            .pipe(
                switchMap((data) => this.sitesDataService.sites.pipe(
                    map((sites: SiteDto[]) => sites.find((site: SiteDto) => site.id === data.id))
                ))
            )

        this.selectedMonument = this.mapListeners.monuments.asObservable()
            .pipe(
                switchMap((data: any) => this.monumentsDataService.monuments.pipe(
                    map((monuments: MonumentDto[]) => monuments.find((monument: MonumentDto) => monument.id === data.id))
                ))
            )
    }

    ngAfterViewInit(): void {
        this.initMap();
    }

    ngOnDestroy(): void {
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    isFavorite(site: SiteDto) {
        const currentUser = this.userDataService.getUser();
        if (currentUser.username) {
            this.userService.saveFavoriteSite(site);
            const siteDataForJson = this.sitesDataService.getSiteData().map(
                (site) => {
                    site['isFavorite'] = this.isSiteFavorite(site);
                    return site;
                })
            const geoJson = this.createGeoJson(siteDataForJson);
            this.map.getSource('sites').setData(geoJson);
        } else {
            this.isLoginOpen = true;
            setTimeout(() => {
                this.isLoginOpen = false;
            })
        }
    }

    isMonument(monument: MonumentDto) {
        const currentUser = this.userDataService.getUser();
        if (currentUser.username) {
            this.userService.saveFavoriteMonument(monument);
            const monumentDataForJson = this.monumentsDataService.getMonumentData().map(
                (monument) => {
                    monument['isFavorite'] = this.isMonumentFavorite(monument);
                    return monument;
                })
            const geoJson = this.createGeoJson(monumentDataForJson);
            this.map.getSource('monuments').setData(geoJson);
        } else {
            this.isLoginOpen = true;
            setTimeout(() => {
                this.isLoginOpen = false;
            })
        }
    }

    isSiteFavorite(site: SiteDto) {
        const currentUser = this.userDataService.getUser();
        if (currentUser.username) {
            return currentUser.favoriteSites.includes(site.id);
        } else {
            return false;
        }
    }

    isMonumentFavorite(monument: MonumentDto) {
        const currentUser = this.userDataService.getUser();
        if (currentUser.username) {
            return currentUser.favoriteMonuments.includes(monument.id);
        } else {
            return false;
        }
    }

    toggleLayer(id) {
        this.mapService.activateClusterLayer(this.map, id, ["sites", "monuments"]);
    }

    isSitePurchased(siteId): Observable<boolean> {
        return this.getSiteFromMonument(siteId).pipe(
            switchMap((site) =>
                iif(() => site.free,
                    of(true),
                    of(this.subscriptionPacketsService.isSitePurchased(site))
                )
            )
        );
    }

    getSiteFromMonument(siteId): Observable<SiteDto> {
        return this.sitesDataService.getSiteById(siteId);
    }

    openMonument(monument) {
        this.flowLoading = true;
        this.currentMonument = null;
        const subscription = combineLatest([this.sitesDataService.getSiteById(monument.siteId), this.subscriptionPacketsService.checkUserPacket(monument.seotitle)])
            .subscribe((value) => {
                this.flowLoading = false;
                if (value[1] || value[0].free) {
                    this.isMonumentOpen = true;
                    this.currentMonument = monument;
                } else {
                    this.noValidPacketHandle(monument);
                }
                subscription.unsubscribe();
            });
    }

    toggleMonument() {
        this.isMonumentOpen = !this.isMonumentOpen;
    }

    private initMonuments() {
        this.$monuments = this.monumentsDataService.getAllMonumentData()
            .pipe(
                tap((data) => {
                    if (!this.map.getLayer("cluster-monuments")) {
                        const geoJsonSite = data.map((monument) => {
                            monument['isFavorite'] = this.isMonumentFavorite(monument);
                            return monument;
                        })
                        this.addGeoJson("monuments", geoJsonSite);
                    }
                    if (this.activeTab === 2) {
                        this.mapService.activateClusterLayer(this.map, "monuments", ["sites", "monuments"]);
                    }
                }),
                map(this.sortMonuments.bind(this)),
                map((monuments: MonumentDto[]) => {
                    return monuments.map((monument: MonumentDto) => {
                        let slider = [monument.centerImage];
                        if (monument.slides.length > 0 && monument.slides[0] !== '') {
                            slider = [...slider, ...monument.slides];
                        }
                        monument.slides = slider;
                        return monument;
                    })
                }),
                tap((monuments) => {
                    this.mapMonumentsToSite = {};
                    for (let monument of monuments) {
                        if (monument.componentConfiguration.threeSixty) {
                            this.mapMonumentsToSite[monument.siteId] = this.mapMonumentsToSite[monument.siteId] ? this.mapMonumentsToSite[monument.siteId] + 1 : 1;
                        }
                    }
                })
            );
    }

    private sortMonuments(monuments) {
        return monuments.sort((a, b) =>
            this.globalDataService.getObjectTranslation(a.monumentTranslations, "name") > this.globalDataService.getObjectTranslation(b.monumentTranslations, "name") ? 1 : -1
        )
    }

    private initMap() {
        this.map = this.mapService.clusterMap();
        this.map.on('load', () => {
            this.initMonuments();
            this.$sites = this.sitesDataService.sites.pipe(
                skipWhile((data: SiteDto[]) => data.length === 0),
                map((data) => data.filter((el) => el.active)),
                tap((data) => {
                    const geoJsonSite = data.map((site) => {
                        site['isFavorite'] = this.isSiteFavorite(site);
                        return site;
                    })
                    this.addGeoJson("sites", geoJsonSite);
                    if (this.activeTab === 1) {
                        this.mapService.activateClusterLayer(this.map, "sites", ["sites", "monuments"]);
                    }
                })
            );
            this.mapService.mapLoadImage(this.map);
            const divSite = document.getElementById('popUpWindow-sites');
            this.mapPopup.sites = new mapboxgl.Popup()
                .setDOMContent(divSite);

            const divMonument = document.getElementById('popUpWindow-monuments');
            this.mapPopup.monuments = new mapboxgl.Popup()
                .setDOMContent(divMonument);
        });
    }

    private addGeoJson(name, data) {
        if (data.length > 0) {
            const geoJson = this.createGeoJson(data);
            this.mapService.addGeoJsonLayer(name, geoJson, this.mapListeners[name], this.mapPopup[name]);
        }
    }

    private createGeoJson(data) {
        const geoJson = new GeoJson();
        geoJson.type = "FeatureCollection";
        geoJson.features = data.map((el) => {
            const geoJSONObject = new GeoJson();
            geoJSONObject.type = "Feature";
            geoJSONObject.geometry = {
                type: "Point",
                coordinates: [el.longitude, el.latitude]
            };
            geoJSONObject.properties = el;
            return geoJSONObject;
        });
        return geoJson;
    }

    private noValidPacketHandle(monument: MonumentDto) {
        if (this.userDataService.getUser().id) {
            this.isPacketOpen = true;
            this.currentSite = this.sitesDataService.getSiteData().find((site) => site.id === monument.siteId);
        } else {
            this.isLoginOpen = false;
            setTimeout(() => {
                this.isLoginOpen = true;
            })
        }
    }

}
